import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useFilterOptions } from "@/features/attorney/views/Entries/filters/useFilterOptions";
import { useAttorneyFilters } from "@/features/attorney/views/Entries/filters/useFilters";
import { useConfiguredTimekeepers } from "@/features/attorney/views/Entries/timekeepers";
import { useEntriesCache, useRestrictedWords } from "@/shared/cache";
import { FEATURE_FLAGS } from "@/shared/config/featureFlags";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlags";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import {
	DEFAULT_FILTER,
	type FilterState,
} from "@/v2/components/FilterPopover";
import {
	DEFAULT_GROUP,
	DEFAULT_SORT,
	type GroupMode,
	type SortMode,
} from "@/v2/components/SortPopover";
import { TimekeeperFilterPopover } from "@/v2/components/TimekeeperFilterPopover";
import { useActions } from "@/v2/contexts/ActionsProvider";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useAttachmentCommit } from "@/v2/hooks/useEntryAttachments";
import { useRubberBand } from "@/v2/hooks/useRubberBand";
import {
	formatDateForApi,
	getAddEntryDefaultDate,
	localDateKey,
} from "@/v2/lib/dates";
import { createAddEntryPayload } from "@/v2/lib/entryPayloads";
import { AddEntryDialog } from "./components/AddEntryDialog";
import { EntriesActionDock } from "./components/EntriesActionDock";
import { EntriesDateToolbar } from "./components/EntriesDateToolbar";
import { EntriesList } from "./components/EntriesList";
import { EntriesSelectionOverlay } from "./components/EntriesSelectionOverlay";
import { useEntriesAutoScroll } from "./hooks/useEntriesAutoScroll";
import { useEntriesPageData } from "./hooks/useEntriesPageData";
import { useEntriesShellContainer } from "./hooks/useEntriesShellContainer";

export default function Entries() {
	useDocumentTitle("Entries");

	const { date, dateRange, setDate, setDateRange } = useDateParams();
	const timekeepers = useConfiguredTimekeepers();
	const fieldOptions = useFilterOptions([], timekeepers);
	const {
		filters: attorneyFilters,
		defaultFilters: attorneyDefaultFilters,
		setTimekeepers,
	} = useAttorneyFilters(fieldOptions);
	const selectedTimekeepers = attorneyFilters.timekeeper ?? [];
	const selectedTimekeeper =
		selectedTimekeepers.length === 1 ? selectedTimekeepers[0] : null;
	const entriesScope = useMemo(() => {
		const from = dateRange.from ?? dateRange.to;
		const to = dateRange.to ?? dateRange.from;
		const dateScope =
			from || to
				? {
						from: localDateKey(from && to && from > to ? to : from),
						to: localDateKey(from && to && from > to ? from : to),
					}
				: { from: localDateKey(date), to: localDateKey(date) };
		return {
			date: dateScope,
			timekeeperIds: attorneyFilters.timekeeper,
			includeAttachments: false,
		};
	}, [date, dateRange.from, dateRange.to, attorneyFilters.timekeeper]);
	const { entries, isLoading } = useEntriesCache(entriesScope);
	const actions = useActions();
	const commitAttachments = useAttachmentCommit();
	const canUploadFiles = useFeatureFlag(FEATURE_FLAGS.FILE_UPLOAD);
	const timer = useTimerContext();

	// The entries cache key includes the provider-backed timekeeper selection and
	// the URL-backed date scope.
	const { getRestrictedWords } = useRestrictedWords();
	const { pageRef, shellContainer, handlePageRef } = useEntriesShellContainer();

	const [addOpen, setAddOpen] = useState(false);
	const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
	const [sort, setSort] = useState<SortMode>(DEFAULT_SORT);
	const [group, setGroup] = useState<GroupMode>(DEFAULT_GROUP);

	useRubberBand(pageRef);

	const { dateFilteredEntries, table, filteredRows, isMultiDate, selection } =
		useEntriesPageData({
			entries,
			date,
			dateRange,
			filter,
			sort,
			group,
			getRestrictedWords,
		});

	const handleEntriesRendered = useEntriesAutoScroll({
		date,
		dateRange,
		filteredEntryCount: filteredRows.length,
		pageRef,
	});

	const timerPrefillDuration = timer.pendingDuration;
	const addDialogOpen = addOpen || timerPrefillDuration !== null;
	const addDefaultDate = getAddEntryDefaultDate(date, dateRange);

	const clearTimerPrefill = useCallback(() => {
		timer.clearPendingDuration();
	}, [timer]);

	const resetAddDialogState = useCallback(() => {
		clearTimerPrefill();
	}, [clearTimerPrefill]);

	const handleAddOpenChange = useCallback(
		(open: boolean) => {
			setAddOpen(open);
			if (!open) resetAddDialogState();
		},
		[resetAddDialogState],
	);

	const submitNewEntry = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			shouldExport: boolean,
			timekeeperId: string | null,
			keepOpen = false,
		) => {
			const dateString = formatDateForApi(values.date);

			if (timerPrefillDuration !== null && !keepOpen) {
				setAddOpen(true);
				timer.dismiss();
				clearTimerPrefill();
			}

			const payload = createAddEntryPayload(values, dateString, {
				timekeeperId,
			});
			actions.addEntry(payload, (newEntryId) => {
				if (!keepOpen) setAddOpen(false);
				if (newEntryId !== null && files.length > 0) {
					void commitAttachments(
						newEntryId,
						{ files, removedIds: [] },
						payload.timekeeperId,
					);
				}
				if (!shouldExport) return;
				if (newEntryId === null) {
					toast.error("Entry added but export could not start");
					return;
				}
				actions.exportEntries([newEntryId], payload.timekeeperId);
			});
		},
		[
			actions,
			clearTimerPrefill,
			commitAttachments,
			timer,
			timerPrefillDuration,
		],
	);

	const handleAddSubmit = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			timekeeperId: string | null,
		) => {
			submitNewEntry(values, files, false, timekeeperId);
		},
		[submitNewEntry],
	);

	const handleAddExport = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			timekeeperId: string | null,
		) => {
			submitNewEntry(values, files, true, timekeeperId);
		},
		[submitNewEntry],
	);

	const handleAddDuplicate = useCallback(
		(
			values: TimeEntryFormValues,
			files: File[],
			timekeeperId: string | null,
		) => {
			submitNewEntry(values, files, false, timekeeperId, true);
		},
		[submitNewEntry],
	);

	const handleAddClick = useCallback(() => {
		setAddOpen(true);
	}, []);

	const handleExport = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		if (!actions.exportEntriesByOwner(selection.selectedEntries)) {
			toast.error("Unable to determine the timekeeper for every entry");
			return;
		}
		selection.exitSelectionMode();
	}, [actions, selection]);

	const handleDuplicate = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		if (!actions.duplicateEntriesByOwner(selection.selectedEntries)) {
			toast.error("Unable to determine the timekeeper for every entry");
			return;
		}
		selection.exitSelectionMode();
	}, [actions, selection]);

	const handleDelete = useCallback(() => {
		if (selection.selectedIds.length === 0) return;
		if (!actions.deleteEntriesByOwner(selection.selectedEntries)) {
			toast.error("Unable to determine the timekeeper for every entry");
			return;
		}
		selection.exitSelectionMode();
	}, [actions, selection]);

	return (
		<div ref={handlePageRef} className="relative flex min-h-full flex-col">
			<EntriesSelectionOverlay
				selectionMode={selection.selectionMode}
				shellContainer={shellContainer}
				selectedCount={selection.selectedCount}
				allCurrentPageSelected={selection.allCurrentPageSelected}
				someCurrentPageSelected={selection.someCurrentPageSelected}
				onSelectAll={selection.selectAll}
				onClose={selection.exitSelectionMode}
			/>

			<EntriesDateToolbar
				date={date}
				dateRange={dateRange}
				onDateChange={setDate}
				onDateRangeChange={setDateRange}
				filter={filter}
				onFilterChange={setFilter}
				sort={sort}
				group={group}
				onSortChange={setSort}
				onGroupChange={setGroup}
				entries={dateFilteredEntries}
				getRestrictedWords={getRestrictedWords}
				timekeeperSlot={
					<TimekeeperFilterPopover
						timekeepers={timekeepers}
						selectedIds={selectedTimekeepers}
						defaultIds={attorneyDefaultFilters.timekeeper ?? []}
						onChange={setTimekeepers}
					/>
				}
			/>

			<div className="flex-1">
				<EntriesList
					isLoading={isLoading}
					entries={entries}
					dateFilteredEntries={dateFilteredEntries}
					table={table}
					isMultiDate={isMultiDate}
					selectionMode={selection.selectionMode}
					getRestrictedWords={getRestrictedWords}
					onIconClick={selection.handleIconClick}
					onRowSelect={selection.handleRowSelect}
					onLongPress={selection.enterSelectionMode}
					onFilterChange={setFilter}
					onEntriesRendered={handleEntriesRendered}
					canUploadFiles={canUploadFiles}
				/>
			</div>

			<AddEntryDialog
				open={addDialogOpen}
				date={addDefaultDate}
				timerPrefillDuration={timerPrefillDuration}
				onOpenChange={handleAddOpenChange}
				onSubmit={handleAddSubmit}
				onExport={handleAddExport}
				onDuplicate={handleAddDuplicate}
				canUploadFiles={canUploadFiles}
				timekeepers={timekeepers}
				defaultTimekeeperId={
					selectedTimekeeper ?? attorneyDefaultFilters.timekeeper?.[0] ?? null
				}
			/>

			<EntriesActionDock
				selectionMode={selection.selectionMode}
				selectedCount={selection.selectedCount}
				onDuplicate={handleDuplicate}
				onDelete={handleDelete}
				onExport={handleExport}
				onAdd={handleAddClick}
			/>
		</div>
	);
}
